/*
 * Mobile Sticky ATC — POC (Baked by Melissa)
 * Task: MOBILE: Sticky ATC button on PDP.
 *
 * Approach: a "mirror" sticky bar. The real Biscuit Bundles pack selector and
 * the real <product-form> add-to-cart button STAY exactly where they are on the
 * page — nothing is moved or pinned, so there is no page jump and no risk to
 * BB's lifecycle. The bar renders lightweight proxy controls:
 *
 *   - Pack chips: built from each real pack card's title / note / price. Clicking
 *     a chip calls the real card's .click(), so all of BB's own selection
 *     handlers fire (price recalculates, conditional add-ons update, etc.).
 *   - ATC button: clicking it calls the real submit button's .click(), so BB's
 *     real add-to-cart runs and submits the correct variant/price.
 *
 * State is READ back from the real controls (never reimplemented): a
 * MutationObserver mirrors the live ATC price + label + disabled state, and the
 * selected pack, out to the bar.
 *
 * Verified on dev: clicking the real elements fires every BB handler; price and
 * cart are correct. Mobile only (<=749px); the bar shows when the on-page ATC
 * scrolls out of view.
 */
(function () {
  var MOBILE = window.matchMedia('(max-width: 749px)');
  var initialized = false;

  function text(el, sel) {
    var n = el.querySelector(sel);
    var t = n ? (n.textContent || '').replace(/\s+/g, ' ').trim() : '';
    return t;
  }

  // Read a pack card's ABSOLUTE price ("$70.00"), which is what Holden's approved
  // design shows on each chip.
  //
  // Deliberately not read from `.biscuits-bundle-item__price` /
  // <biscuits-bundle-price>: once a pack is selected BB re-renders those into an
  // UPGRADE DELTA relative to the current selection ("+$33.00"), and they are empty
  // before any selection at all. Both would put the wrong number on the chip —
  // verified on the dev store, where the 50-pack card reads "+$33.00" in the price
  // node and "$70.00" in the card body.
  //
  // So: take the first currency string in the card that is not prefixed with "+".
  // syncPacks() re-reads this on every mutation, so a chip cannot hold a stale or
  // blank price if BB hydrates late.
  var CURRENCY = /(\+\s*)?[$£€]\s?[\d,]+(?:\.\d{2})?/g;
  function readPrice(card) {
    var text = (card.textContent || '').replace(/\s+/g, ' ');
    CURRENCY.lastIndex = 0;
    var m;
    var firstAny = '';
    while ((m = CURRENCY.exec(text)) !== null) {
      if (!firstAny) firstAny = m[0].trim();
      if (!m[1]) return m[0].trim(); // no "+" prefix — an absolute price
    }
    return firstAny; // only deltas present; better than showing nothing
  }

  // Locate the add-on / "Complete your gift" section to scroll to, by heading text.
  //
  // Two passes, and RENDERED steps only. Half the product templates (10 of the 20
  // that render this bar) set `hide_first_bundle_step`, which display:none's
  // [data-biscuits-step="1"] from main-product.liquid while leaving its heading in
  // the DOM — and on the gift-box templates that hidden step is titled
  // "Gift Box [HIDDEN FROM USERS]". A single-pass document-order match therefore
  // picked the hidden step and scrollIntoView() did nothing, on exactly the
  // high-AOV pages the link exists to serve. So: skip any step with no layout boxes.
  //
  // The strong pass runs first because those pages carry BOTH the hidden
  // "Gift Box ..." step and the real "COMPLETE YOUR GIFT..." step, and document
  // order puts the wrong one first even once the hidden one is excluded on other
  // templates.
  //
  // No fallback to "the first non-pack step": on the assortment templates that step
  // is a REQUIRED flavour picker, so the bar would advertise "Want to add a gift box
  // or card?" and scroll the shopper to something else entirely. If no heading
  // matches we render no link at all — a missing nicety beats a wrong destination.
  var ADDON_STRONG = /complete your gift/i;
  var ADDON_LOOSE = /gift box|add[- ]?on|card/i;
  function findAddonSection() {
    var titles = Array.prototype.slice.call(
      document.querySelectorAll('.biscuits-bundle-step__title')
    );
    function pick(re) {
      for (var i = 0; i < titles.length; i++) {
        if (!re.test(titles[i].textContent || '')) continue;
        var step = titles[i].closest('[data-biscuits-step]') || titles[i];
        // getClientRects() is empty for a display:none subtree. Scrolling to one
        // is a silent no-op, which is worse than showing no link.
        if (!step.getClientRects().length) continue;
        return step;
      }
      return null;
    }
    return pick(ADDON_STRONG) || pick(ADDON_LOOSE);
  }

  function init() {
    if (initialized) return;
    var realAtc = document.querySelector('[id^="ProductSubmitButton-"]');
    if (!realAtc) return;
    var realForm = realAtc.closest('form');
    if (!realForm) return;
    initialized = true;

    var packStep =
      document.querySelector('[data-biscuits-step="1"]') ||
      document.querySelector('biscuits-related-products');

    // Only mirror step 1 when it is genuinely a pack CHOOSER: a single-select
    // step (data-biscuits-selection="s") offering more than one option. On the
    // gift-box / assortment templates step 1 is a multi-select quantity step
    // holding the gift box itself, and mirroring it produced a chip built from
    // the card's whole textContent — including BB's inline JSON config. Those
    // products fall back to an ATC-only bar, which is also what the client asked
    // for: "for the gift box + assortment, we would default to the preselected
    // pack" (Farrah, 2026-06-01). Matches biscuit-bundler.js, which likewise
    // bails on any step whose selection mode isn't "s".
    var isPackChooser = !!packStep && packStep.dataset.biscuitsSelection === 's';
    var realCards = isPackChooser
      ? Array.prototype.slice.call(packStep.querySelectorAll('.biscuits-bundle-item-card'))
      : [];
    if (realCards.length < 2) realCards = [];

    var bar = document.createElement('div');
    bar.className = 'msatc';
    // Duplicates on-page controls; hide from assistive tech to avoid double reads.
    bar.setAttribute('aria-hidden', 'true');

    // ---- Jump link to the add-on / "Complete your gift" section ----
    // Placed above the chips so it can't be mis-tapped next to the ATC button.
    var addonSection = findAddonSection();
    if (addonSection) {
      var addonLink = document.createElement('button');
      addonLink.type = 'button';
      addonLink.className = 'msatc__addon-link';
      addonLink.tabIndex = -1;
      addonLink.textContent = 'Want to add a gift box or card?';
      addonLink.addEventListener('click', function (e) {
        e.preventDefault();
        // Re-resolved on every tap rather than trusting the node captured at init:
        // Biscuit Bundles re-renders its steps after hydration, and a step that was
        // visible when the bar was built can be hidden by the time it's tapped.
        var target = findAddonSection();
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      bar.appendChild(addonLink);
    }

    // ---- Pack chips (proxy to the real cards) ----
    // Every card must expose a real title, or we render no chips at all rather
    // than a bar full of placeholder labels.
    if (
      realCards.length &&
      !realCards.every(function (real) {
        return !!text(real, '.biscuits-bundle-item__product-title');
      })
    ) {
      realCards = [];
    }

    var pairs = [];
    if (realCards.length) {
      var packs = document.createElement('div');
      packs.className = 'msatc__packs';
      realCards.forEach(function (real) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'msatc__pack';
        chip.tabIndex = -1;

        // No textContent fallback: BB renders an inline JSON config blob inside
        // the card, so a fallback puts that whole payload in the chip label.
        var title = text(real, '.biscuits-bundle-item__product-title');
        var note = text(real, '.biscuits-bundle-item__text-metafield');

        chip.innerHTML =
          '<span class="msatc__pack-title"></span>' +
          (note ? '<span class="msatc__pack-note"></span>' : '') +
          '<span class="msatc__pack-price"></span>';
        chip.querySelector('.msatc__pack-title').textContent = title;
        if (note) chip.querySelector('.msatc__pack-note').textContent = note;
        chip.querySelector('.msatc__pack-price').textContent = readPrice(real);

        // BB's selection handler lives on the inner .biscuits-bundle-item element
        // (the card wrapper doesn't respond to a programmatic click).
        var realSelectable = real.querySelector('.biscuits-bundle-item') || real;
        chip.addEventListener('click', function (e) {
          e.preventDefault();
          realSelectable.click(); // fire BB's real selection handlers
        });

        packs.appendChild(chip);
        pairs.push({ chip: chip, real: real });
      });
      bar.appendChild(packs);
    }

    // ---- ATC button (proxy to the real submit button) ----
    var atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'msatc__atc button button--full-width button--primary';
    // Like the chips and the jump link: the whole bar is aria-hidden because it
    // duplicates on-page controls, so nothing inside it may take keyboard focus.
    // Without this the proxy ATC is the last tab stop in the document while being
    // absent from the accessibility tree (axe `aria-hidden-focus`). The real button
    // is still reachable in its normal place in the form.
    atc.tabIndex = -1;
    var atcLabel = document.createElement('span');
    atcLabel.className = 'msatc__atc-label';
    atc.appendChild(atcLabel);
    // Mirror the real button's loading spinner (as a direct child, which the
    // theme's `.button.loading > .loading__spinner` CSS expects).
    var realSpinner = realAtc.querySelector('.loading__spinner');
    if (realSpinner) atc.appendChild(realSpinner.cloneNode(true));
    atc.addEventListener('click', function () {
      // Don't fire while the real button is mid add-to-cart (avoids double submit).
      if (realAtc.disabled || realAtc.getAttribute('aria-disabled') === 'true') return;
      realAtc.click(); // fire BB's real add-to-cart
    });
    bar.appendChild(atc);

    document.body.appendChild(bar);

    // ---- Sync state FROM the real controls ----
    function isActive(card) {
      // Check the item element's OWN class — a descendant <img> also carries
      // `biscuits--active` permanently, so a subtree query gives false positives.
      var item = card.querySelector('.biscuits-bundle-item') || card;
      return item.classList.contains('biscuits--active');
    }
    function syncAtc() {
      // PRC 10.2026: on Simple Product / default templates the price span comes
      // after the text span in the markup (flex order shows it first), so read
      // the two spans rather than the button's text in markup order.
      var priceSpan = realAtc.querySelector('.product-form__submit-price');
      var textSpan = realAtc.querySelector('.product-form__submit-text');
      var label = priceSpan && textSpan && getComputedStyle(priceSpan).display !== 'none'
        ? priceSpan.textContent + ' ' + textSpan.textContent
        : realAtc.textContent;
      atcLabel.textContent = (label || '').replace(/\s+/g, ' ').trim();
      atc.disabled = realAtc.disabled;
      // Mirror the loading state (class + aria + spinner visibility).
      var loading = realAtc.classList.contains('loading');
      atc.classList.toggle('loading', loading);
      atc.setAttribute(
        'aria-disabled',
        loading || realAtc.getAttribute('aria-disabled') === 'true' ? 'true' : 'false'
      );
      var ms = atc.querySelector('.loading__spinner');
      var rs = realAtc.querySelector('.loading__spinner');
      if (ms && rs) ms.classList.toggle('hidden', rs.classList.contains('hidden'));
    }
    function syncPacks() {
      pairs.forEach(function (p) {
        p.chip.classList.toggle('msatc__pack--active', isActive(p.real));
        // Re-read the price rather than trusting the value captured at init: BB
        // hydrates and re-renders the price node, so it can be empty at the moment
        // the chip is built, and could change if pricing ever became dynamic.
        var priceEl = p.chip.querySelector('.msatc__pack-price');
        var price = readPrice(p.real);
        if (priceEl && price && priceEl.textContent !== price) priceEl.textContent = price;
      });
    }
    syncAtc();
    syncPacks();

    var mo = new MutationObserver(function () {
      syncAtc();
      syncPacks();
    });
    mo.observe(realAtc, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
    if (packStep) {
      // characterData/childList too, so a price hydrating late re-syncs the chips.
      mo.observe(packStep, { subtree: true, attributes: true, childList: true, characterData: true });
    }

    // ---- Show the bar only once the on-page ATC has scrolled PAST (mobile) ----
    function setVisible(show) {
      var visible = !!show && MOBILE.matches;
      bar.classList.toggle('msatc--visible', visible);
      // Publish the bar's height so the chat launcher can lift clear of it.
      // Measured, not hard-coded: the bar is 88px on a plain product and ~163px
      // with pack chips.
      var h = visible ? Math.round(bar.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--msatc-h', h + 'px');
    }
    // Observe the ATC button itself (not the whole form — BB appends add-on steps
    // below the button inside the form, which would delay the bar until they too
    // scroll off). This shows the bar the instant the real ATC leaves the screen.
    var atcTarget = realAtc.closest('.product-form__buttons') || realAtc;

    // "Off screen" is not enough: on landing, the ATC is BELOW the fold and also
    // off screen, which put the bar up over the product name and the "starting at"
    // price (client QA, 2026-07-15). Only show it once the ATC has scrolled ABOVE
    // the viewport — the Goldbelly behaviour.
    function update() {
      // A detached node reports an all-zero DOMRect, which reads as "scrolled above
      // the viewport" and would pin the bar open over the product title — the exact
      // regression client QA rejected on 2026-07-15. Reachable in the theme editor,
      // where changing a Product-information setting replaces the section DOM while
      // this bar (appended to <body>) survives holding stale references.
      if (!atcTarget.isConnected) {
        setVisible(false);
        return;
      }
      setVisible(atcTarget.getBoundingClientRect().bottom <= 0);
    }

    // Driven by scroll rather than by IntersectionObserver alone: an instant jump
    // (header logo, browser scroll restoration) can take the ATC from above the
    // viewport to below it without ever intersecting, so IO reports no threshold
    // crossing and the bar would stay stuck open at the top of the page.
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // The bundle steps render async and move the ATC down the page; re-check when
    // its position changes without a scroll.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(update, { threshold: 0 }).observe(atcTarget);
    }
    update();
    if (MOBILE.addEventListener) {
      MOBILE.addEventListener('change', function () {
        if (!MOBILE.matches) bar.classList.remove('msatc--visible');
      });
    }

    // ---- Bring add-to-cart errors back into view ----
    //
    // The error message renders inside the form, which by definition is scrolled
    // off the top of the screen whenever this bar is up. So a rejected /cart/add
    // — inventory, a quantity rule, a sold-out add-on step — looked to the shopper
    // like the button simply did nothing. Worse, product-form.js clears
    // aria-disabled in its `finally`, so the bar's button goes live again and
    // every retry silently re-fails.
    //
    // Subscribing to the pub/sub event rather than watching the wrapper: product-form.js
    // publishes cartError BEFORE calling handleErrorMessage(), which early-returns
    // entirely when the form carries data-hide-errors (the gift-card template). The
    // event is the only signal present in every case.
    //
    // Scrolling the form back into view also brings the real ATC on screen, which
    // hides this bar — so the shopper is handed back to the real control rather
    // than left tapping a proxy that can't explain itself.
    var productForm = realAtc.closest('product-form');
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartError, function () {
        // Only when the bar is up. If the shopper used the real button they are
        // already looking at the message, and yanking the page would be worse.
        if (!bar.classList.contains('msatc--visible')) return;
        // A tick later: the event is published before the message is written and
        // un-hidden. setTimeout rather than rAF, which never fires in a background tab.
        setTimeout(function () {
          var wrapper =
            productForm && productForm.querySelector('.product-form__error-message-wrapper');
          // Fall back to the button itself when errors are suppressed on this
          // template — that is where the sold-out message renders.
          var target = wrapper && !wrapper.hasAttribute('hidden') ? wrapper : atcTarget;
          if (target && target.isConnected) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 0);
      });
    }
  }

  /*
   * Lift the chat launcher clear of the sticky bar.
   *
   * Two different widgets are in play and BOTH have to be handled: the live theme
   * runs Applied Labs (<applied-chat>), while this branch/dev still has Zendesk
   * (iframe#launcher). Zendesk is plain light DOM and is handled by a rule in
   * mobile-sticky-atc.css. Applied Labs renders its launcher inside a shadow root,
   * where an external stylesheet cannot reach — but the root is OPEN, so we can
   * inject a stylesheet into it.
   *
   * Both get BOTH margins, which is self-selecting: the two widgets anchor
   * differently and each margin is inert on the other kind of element.
   *   - Applied Labs anchors by TOP, via inline
   *     `top: clamp(24px, 100vh, -24px + 100vh); transform: translateY(-100%)`.
   *     A top-anchored box moves up with a negative margin-top; margin-bottom
   *     does nothing to it (verified — it resolved `bottom` to -193px and the
   *     launcher did not budge).
   *   - Zendesk anchors by BOTTOM (iframe#launcher, bottom: 0), where the reverse
   *     holds: margin-bottom lifts it, margin-top does nothing.
   * Margins rather than `top`/`bottom`/`transform` because the anchor values are
   * inline (so a stylesheet would need !important and would have to reproduce
   * Applied's clamp expression), and both widgets animate their own transform.
   * `--msatc-h` is set on <html> by setVisible() and is 0 whenever the bar is
   * hidden or we're on desktop, so this is inert everywhere else.
   */
  function liftChatLauncher() {
    var STYLE_ID = 'msatc-chat-offset';
    function injectInto(host) {
      if (!host || !host.shadowRoot) return false;
      if (host.shadowRoot.getElementById(STYLE_ID)) return true;
      var style = document.createElement('style');
      style.id = STYLE_ID;
      // Custom properties inherit across the shadow boundary, so --msatc-h set on
      // <html> is readable in here.
      style.textContent =
        '.applied-chat-root > .fixed {' +
        '  margin-top: calc(-1 * var(--msatc-h, 0px));' +
        '  margin-bottom: var(--msatc-h, 0px);' +
        '  transition: margin-top 0.15s ease-out, margin-bottom 0.15s ease-out;' +
        '}';
      host.shadowRoot.appendChild(style);
      return true;
    }

    if (injectInto(document.querySelector('applied-chat'))) return;
    // Poll rather than observe. What we are waiting for is attachShadow() on an
    // element that may already exist — and attaching a shadow root produces NO
    // light-DOM mutation record, so a MutationObserver can miss it entirely and
    // sit there until it gives up. Give up after ~20s: on the corporate-gifts
    // template the widget is deliberately absent (see layout/theme.liquid).
    var tries = 80;
    (function poll() {
      if (injectInto(document.querySelector('applied-chat'))) return;
      if (--tries > 0) setTimeout(poll, 250);
    })();
  }
  liftChatLauncher();

  document.addEventListener('BiscuitsBundleForm:ready', function () {
    requestAnimationFrame(init);
  });

  function fallback(tries) {
    if (initialized) return;
    if (document.querySelector('[id^="ProductSubmitButton-"]')) {
      init();
      return;
    }
    if (tries > 0) setTimeout(function () { fallback(tries - 1); }, 200);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { fallback(20); });
  } else {
    fallback(20);
  }
})();
